import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { authApi } from '../utils/authApi';
import { API_URL } from '../utils/config';
import {
  clearRecoveryCredentials,
  hasValidRecoveryCredentials,
  loadRecoveryCredentials,
  type RecoveryCredentials,
} from '../utils/recoveryTokens';
import './Auth.css';

function resolveRecoveryState(): {
  recoveryCreds: RecoveryCredentials | null;
  tokenError: string;
} {
  const creds = loadRecoveryCredentials();

  if (creds.error) {
    return {
      recoveryCreds: null,
      tokenError:
        creds.errorDescription ||
        creds.error ||
        'This reset link is invalid or has expired. Please request a new password reset email.',
    };
  }

  if (!hasValidRecoveryCredentials(creds)) {
    return {
      recoveryCreds: null,
      tokenError:
        'This reset link is missing a token or has expired. Request a new reset email and open the link once.',
    };
  }

  return { recoveryCreds: creds, tokenError: '' };
}

const ConfirmResetPassword: React.FC = () => {
  const initialRecovery = resolveRecoveryState();
  const [recoveryCreds] = useState<RecoveryCredentials | null>(initialRecovery.recoveryCreds);
  const [tokenError] = useState(initialRecovery.tokenError);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    if (!recoveryCreds) {
      setError('Missing reset token. Please request a new reset link.');
      setLoading(false);
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters long');
      setLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    try {
      const response = await authApi.post('/auth/confirm-reset-password', {
        token_hash: recoveryCreds.tokenHash || undefined,
        access_token: recoveryCreds.accessToken || undefined,
        refresh_token: recoveryCreds.refreshToken || undefined,
        password,
        confirm_password: confirmPassword,
      });

      if (response.data.success) {
        clearRecoveryCredentials();
        setSuccess(response.data.message || 'Password updated successfully.');
        setTimeout(() => {
          navigate('/login', { replace: true });
        }, 2000);
      } else {
        setError(response.data.error || 'Failed to update password');
      }
    } catch (err: unknown) {
      const axiosErr = axios.isAxiosError(err) ? err : null;
      const msg =
        axiosErr?.response?.data?.error ||
        (axiosErr?.code === 'ERR_NETWORK' || axiosErr?.message === 'Network Error'
          ? `Cannot reach the API at ${API_URL}. Is the backend running?`
          : null) ||
        axiosErr?.message ||
        'An error occurred while updating your password';
      setError(typeof msg === 'string' ? msg : 'An error occurred while updating your password');
    } finally {
      setLoading(false);
    }
  };

  if (tokenError) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <h1>Forensic AI</h1>
            <p>Reset link invalid</p>
          </div>
          <div className="message error">{tokenError}</div>
          <div className="auth-footer">
            <p>
              <Link to="/reset-password" className="auth-link">
                Request a new reset link
              </Link>
            </p>
            <p style={{ marginTop: '12px' }}>
              <Link to="/login" className="auth-link">
                Back to sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h1>Forensic AI</h1>
          <p>Choose a new password</p>
        </div>

        {error && <div className="message error">{error}</div>}

        {success && <div className="message success">{success}</div>}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="password">New Password</label>
            <div className="password-input-container">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="auth-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="password-toggle"
              >
                <i className={`fas fa-${showPassword ? 'eye-slash' : 'eye'}`}></i>
              </button>
            </div>
            <p className="password-hint">Password must be at least 6 characters long</p>
          </div>

          <div className="form-group">
            <label htmlFor="confirm_password">Confirm New Password</label>
            <div className="password-input-container">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirm_password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="auth-input"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="password-toggle"
              >
                <i className={`fas fa-${showConfirmPassword ? 'eye-slash' : 'eye'}`}></i>
              </button>
            </div>
          </div>

          <button type="submit" disabled={loading || !!success} className="auth-button">
            {loading ? (
              <div className="loading-spinner">
                <div className="spinner"></div>
              </div>
            ) : (
              'Update Password'
            )}
          </button>
        </form>

        <div className="auth-footer">
          <p>
            <Link to="/login" className="auth-link">
              Back to sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ConfirmResetPassword;
